import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { DataGridComponent, Button } from '@components/common'
import { ColumnModel, GridComponent } from '@syncfusion/ej2-react-grids'
import { RowSelectEventArgs } from '@syncfusion/ej2-grids'
import { TextAlign } from '@constants/Common/GridComponent.contants'
import { IBlockedUser } from '@dataTypes/blocked-user.type'
import { formatPhoneNumber } from '@utils/numberFormatter.util'
import { useAppSelector } from '@store/hooks'
import moment from 'moment'
import type { FaxPreviewHooks } from './useFaxPreview'

type BlockedGridRow = {
  blocked_user_id: string
  phone_display: string
  contact_name: string
  email: string
  date_display: string
  blockedUser: IBlockedUser
}

type Props = {
  hooks: FaxPreviewHooks
}

const FaxBlockedTable: React.FC<Props> = ({ hooks }) => {
  const gridRef = useRef<GridComponent | null>(null)
  const isSyncingRef = useRef(false)
  const contacts = useAppSelector((state) => state.contacts.data)

  const contactsById = useMemo(() => {
    const map = new Map<string, (typeof contacts)[number]>()
    contacts.forEach((contact) => {
      if (contact.contact_id) map.set(contact.contact_id, contact)
    })
    return map
  }, [contacts])

  const gridData: BlockedGridRow[] = useMemo(
    () =>
      hooks.filteredBlockedUsers.map((user) => {
        const contact = user.contact_id
          ? contactsById.get(user.contact_id)
          : undefined
        const contactName = contact
          ? `${contact.first_name ?? ''} ${contact.last_name ?? ''}`.trim()
          : ''
        return {
          blocked_user_id: user.blocked_user_id ?? '',
          phone_display:
            formatPhoneNumber(user.phone_number ?? '') ||
            user.phone_number ||
            '-',
          contact_name: contactName || '-',
          email: contact?.email?.trim() || '-',
          date_display: user.date_time_created_at
            ? moment(user.date_time_created_at).format('MMM D, YYYY - h:mm A')
            : '-',
          blockedUser: user,
        }
      }),
    [hooks.filteredBlockedUsers, contactsById],
  )

  const rowSelected = useCallback(
    (args: RowSelectEventArgs) => {
      if (isSyncingRef.current) return
      const row = args.data as BlockedGridRow
      if (row?.blockedUser) {
        hooks.toggleBlockedUserSelected(row.blockedUser)
      }
    },
    [hooks],
  )

  const rowDeselected = useCallback(
    (args: RowSelectEventArgs) => {
      if (isSyncingRef.current) return
      const row = args.data as BlockedGridRow
      if (row?.blockedUser) {
        hooks.toggleBlockedUserSelected(row.blockedUser)
      }
    },
    [hooks],
  )

  const syncGridSelection = useCallback(() => {
    const grid = gridRef.current
    const selection = grid?.selectionModule
    if (!grid || !selection) return
    let view: BlockedGridRow[] = []
    try {
      view = (grid.getCurrentViewRecords() as BlockedGridRow[]) ?? []
    } catch {
      return
    }
    const selectedIds = new Set(
      hooks.selectedBlockedUsers.map((u) => u.blocked_user_id),
    )
    const desired: number[] = []
    view.forEach((row, index) => {
      if (row.blocked_user_id && selectedIds.has(row.blocked_user_id)) {
        desired.push(index)
      }
    })
    const current = grid.getSelectedRowIndexes() ?? []
    const alreadySynced =
      desired.length === current.length &&
      desired.every((index) => current.includes(index))
    if (alreadySynced) return
    isSyncingRef.current = true
    try {
      selection.clearSelection()
      if (desired.length > 0) selection.selectRows(desired)
    } catch {
      /* grid not ready */
    }
    setTimeout(() => {
      isSyncingRef.current = false
    }, 0)
  }, [hooks.selectedBlockedUsers])

  useEffect(() => {
    syncGridSelection()
  }, [syncGridSelection, gridData])

  const columns: ColumnModel[] = useMemo(
    () => [
      { field: 'phone_display', headerText: 'Fax number', width: 0.22 },
      { field: 'contact_name', headerText: 'Contact name', width: 0.22 },
      { field: 'email', headerText: 'Email address', width: 0.28 },
      { field: 'date_display', headerText: 'Date blocked', width: 0.2 },
      {
        field: 'actions',
        headerText: 'Actions',
        width: 0.12,
        textAlign: TextAlign.Center,
        allowSorting: false,
        template: (row: BlockedGridRow) => (
          <Button
            size="sm"
            type="button"
            className="act-button"
            title="Unblock"
            disabled={!row.blocked_user_id}
            onClick={() => hooks.handleUnblockFax(row.blockedUser)}
          >
            <span className="mgc_corner_up_left_line" />
          </Button>
        ),
      },
    ],
    [hooks],
  )

  return (
    <div className="fax-datagrid fax-blocked-table">
      <DataGridComponent
        ref={gridRef}
        data={gridData}
        columns={columns}
        allowPaging={true}
        allowSorting={true}
        showCheckbox={true}
        showCheckboxWidth="0.04"
        rowSelected={rowSelected}
        rowDeselected={rowDeselected}
        dataBound={syncGridSelection}
        customCSS="fax-blocked-grid"
      />
    </div>
  )
}

export default FaxBlockedTable
